// Sugalarm — native social sign-in bridge (cycle 154, iOS only).
//
// Wraps @capgo/capacitor-social-login for the native iOS shell. The
// plugin is reached via `window.Capacitor.Plugins.SocialLogin` — NEVER
// a bare dynamic import("@capgo/...") — per the cycle-138 rule: the
// pure-remote webview has no bundler mapping for plugin packages, so
// only the runtime-injected Plugins registry works.
//
// Google needs an iOS OAuth client (the OPERATOR mints it — see
// UPDATE_INSTRUCTIONS.md Cycle 154): the placeholder below must be
// replaced with the real iOS client ID, and its REVERSED form goes in
// the shell's Info.plist URL schemes. Until it's replaced, the Google
// button simply doesn't render on iOS (`nativeGoogleReady()` false).
// The ID token is audienced to the SERVER (web) client ID — passed as
// iOSServerClientId — which is already in GOOGLE_OAUTH_CLIENT_IDS, so
// the backend accepts it with no secret change.
//
// Apple needs nothing beyond the app entitlement: the native sheet
// returns an identityToken audienced to the bundle ID, already in
// APPLE_OAUTH_CLIENT_IDS.

import { capacitorPlatform, isCapacitorNative } from "./capacitor";

// The operator-minted iOS OAuth client (Google Cloud Console →
// Credentials → OAuth client ID → iOS, bundle dev.sugalarm.app;
// substituted 2026-06-12). Public identifier — safe in source. Its
// REVERSED form is the URL scheme in mobile/ios/App/App/Info.plist;
// keep the two in lockstep if it's ever rotated.
const GOOGLE_IOS_CLIENT_ID =
  "678569361182-nv5q54vagvkv284ins29fd2mdccefcm9.apps.googleusercontent.com";

interface SocialLoginPlugin {
  initialize(options: {
    google?: {
      iOSClientId?: string;
      iOSServerClientId?: string;
      mode?: "online" | "offline";
    };
    apple?: Record<string, never>;
  }): Promise<void>;
  login(options: {
    provider: "google" | "apple";
    options: Record<string, unknown>;
  }): Promise<{
    provider: string;
    result: {
      idToken: string | null;
      profile?: {
        givenName?: string | null;
        familyName?: string | null;
        name?: string | null;
      };
    };
  }>;
}

function plugin(): SocialLoginPlugin | null {
  const w = window as unknown as {
    Capacitor?: { Plugins?: { SocialLogin?: SocialLoginPlugin } };
  };
  return w.Capacitor?.Plugins?.SocialLogin ?? null;
}

/** True on the native iOS shell with the plugin present. */
export function nativeSocialAvailable(): boolean {
  return (
    isCapacitorNative() && capacitorPlatform() === "ios" && plugin() !== null
  );
}

/** Google additionally requires the operator-substituted iOS client ID. */
export function nativeGoogleReady(): boolean {
  return (
    nativeSocialAvailable() && !GOOGLE_IOS_CLIENT_ID.startsWith("__")
  );
}

let initialized = false;

/** Initialize once. `serverClientId` is the web/server Google client ID
 *  (from GET /api/auth/providers) — it becomes the ID token's `aud`. */
async function ensureInitialized(serverClientId: string | null): Promise<void> {
  const p = plugin();
  if (!p || initialized) return;
  const opts: Parameters<SocialLoginPlugin["initialize"]>[0] = { apple: {} };
  if (!GOOGLE_IOS_CLIENT_ID.startsWith("__")) {
    opts.google = {
      iOSClientId: GOOGLE_IOS_CLIENT_ID,
      iOSServerClientId: serverClientId ?? undefined,
      mode: "online",
    };
  }
  await p.initialize(opts);
  initialized = true;
}

export interface NativeLoginResult {
  token: string;
  /** Apple sends the name only on FIRST authorization — null after. */
  displayName: string | null;
}

/** True when the thrown error is the user dismissing the native sheet. */
export function isUserCancelled(e: unknown): boolean {
  const msg = (e instanceof Error ? e.message : String(e)).toLowerCase();
  return (
    msg.includes("cancel") || msg.includes("canceled") || msg.includes("1001")
  );
}

export async function nativeLogin(
  provider: "google" | "apple",
  serverClientId: string | null,
): Promise<NativeLoginResult> {
  const p = plugin();
  if (!p) throw new Error("Native sign-in is unavailable");
  await ensureInitialized(serverClientId);
  const resp = await p.login({
    provider,
    options: provider === "apple" ? { scopes: ["name", "email"] } : {},
  });
  const token = resp.result?.idToken;
  if (!token) throw new Error("No identity token returned");
  const prof = resp.result.profile;
  const name =
    prof?.name ||
    [prof?.givenName, prof?.familyName].filter(Boolean).join(" ") ||
    null;
  return { token, displayName: name };
}
